import { useContext, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Button, Layout, Menu, Row, Popover, Skeleton } from "antd";
import { HistoryOutlined, LogoutOutlined, TeamOutlined, UploadOutlined, SolutionOutlined } from "@ant-design/icons";

import { AuthManageContext } from "providers";
import { BannerOfTheEnvIs, HeaderToGlobalLayout } from "components";

import {
  routeBulkActions,
  routeBulkCommonInvoicing,
  routeBulkInvoicing,
  routeCertificationsDelivery,
  routeDigitalStamps,
  routeHistoricRetentions,
  routeHome,
  routeIssuancesInProgress,
  routePartnerCertificates,
  routeTaxpayers,
  routeUnifiedIssuing,
  routeUploadFiles,
} from "routes/pages";

import { WEB_VERSION_DATE, WEB_VERSION_NUMBER } from "constants/commons";
import { getWhiteList } from "constants/whiteList";
import { useAuthMfaSecure } from "providers/AuthSecureManage/AuthMfaSecureManage";
import { SessionManageContext } from "providers/AuthManage/SessionManage";
import { Modules, UserModules } from "types/modules";

export function GeneralLayout(props): JSX.Element {
  const { children } = props;
  const {  listModules } = useAuthMfaSecure();
  const { submitLogout } = useContext(SessionManageContext);
  const [userId, setUserId] = useState<number | null>(null);
  
  const [modules, setModules] = useState<Modules[]>([]);
  const [isLoadingModules, setIsLoadingModules] = useState(true);
  const [logoUrl, setLogoUrl] = useState<string>('');
  const user = { modules };

  const canAccessRTCertifications = user?.modules.some((module) => module?.type === 'rt_certifications');
  const canAccessPartnerCertificates = user?.modules.some((module) => module?.type === 'partner_certifications');
  const canAccessUploadFiles = user?.modules.some((module) => module?.type === 'upload_files');
  const canAccessCertificationsDelivery = user?.modules.some((module) => module?.type === 'certifications_delivery');
  const canAccessHistoricRetentions = user?.modules.some((module) => module?.type === 'historic_retentions');
  const canAccessBulkActions = user?.modules.some((module) => module?.type === 'certification');
  const canAccessBulkInvoicing = user?.modules.some((module) => module?.type === 'fc3');
  const canAccessBulkCommonInvoicing = user?.modules.some((module) => module?.type === 'common_invoices');
  const canAccessIssuancesInProgress = user?.modules.some((module) => module?.type === 'issuances_in_progress');
  const canAccessUploadCSDFiles = user?.modules.some((module) => module?.type === 'upload_csd_files');
  useEffect(() => {
    const storedUserId = sessionStorage.getItem("user_id");
    if (storedUserId) {
      setUserId(parseInt(storedUserId, 10));
    }
  }, []);

  useEffect(() => {
    let isMounted = true;
    
    const fetchModules = async () => {
      try {
        setIsLoadingModules(true);
        const result = await listModules();
        if (isMounted) {
          const listModulesData: UserModules[] = result;
          const modules = listModulesData.map((module) => module.modules);
          setModules(modules);
          
          if (listModulesData && listModulesData.length > 0) {
            setLogoUrl(listModulesData[0].partner?.logo_url || '');
          }
        }
      } catch (error) {
        console.error('Error fetching modules:', error);
      } finally {
        if (isMounted) {
          setIsLoadingModules(false);
        }
      }
    };

    fetchModules();

    return () => {
      isMounted = false;
    };
  }, [listModules]);

  return (
    <Layout style={{ minHeight: "100vh" }}>
      <BannerOfTheEnvIs />
      <HeaderToGlobalLayout />
      <Layout>
        <Layout.Sider width={265} breakpoint="lg" collapsedWidth={0} theme="light">
          <Row justify="center" style={{ margin: "1rem auto 1.5rem" }}>
            <Link to={routeHome()}>
              {isLoadingModules ? (
                <Skeleton.Avatar active size={85} shape="square" />
              ) : (
                <img
                  alt="logo"
                  style={{ width: 85 }}
                  src={logoUrl}
                />
              )}
            </Link>
          </Row>

          {isLoadingModules ? (
            <div style={{ padding: "0 1rem" }}>
              <Skeleton active paragraph={{ rows: 6 }} />
            </div>
          ) : (
            <>
              <Menu mode="inline">
                {canAccessRTCertifications && (
                  <Menu.Item key={routeTaxpayers()} icon={<TeamOutlined />}>
                    <Link to={routeTaxpayers()}>Buscar certificados de RTs</Link>
                  </Menu.Item>
                )}

                {canAccessPartnerCertificates && (
                  <Menu.Item key={routePartnerCertificates()} icon={<TeamOutlined />}>
                    <Link to={routePartnerCertificates()}>Buscar certificados de aliados</Link>
                  </Menu.Item>
                )}

                {canAccessUploadFiles && (
                  <Menu.Item key={routeUploadFiles()} icon={<UploadOutlined />}>
                    <Link to={routeUploadFiles()}>Archivos cargados</Link>
                  </Menu.Item>
                )}

                {canAccessCertificationsDelivery && (
                  <Menu.Item key={routeCertificationsDelivery()} icon={<SolutionOutlined />}>
                    <Link to={routeCertificationsDelivery()}>Entrega de certificaciones</Link>
                  </Menu.Item>
                )}

                {canAccessHistoricRetentions && (
                  <Menu.Item key={routeHistoricRetentions()} icon={<HistoryOutlined />}>
                    <Link to={routeHistoricRetentions()}>Buscar certificados</Link>
                  </Menu.Item>
                )}

                {canAccessBulkActions && false && (
                  <Menu.Item key={routeBulkActions()} icon={<SolutionOutlined />}>
                    <Link to={routeBulkActions()}>Certificación masiva</Link>
                  </Menu.Item>
                )}

                {canAccessBulkInvoicing && false && (
                  <Menu.Item key={routeBulkInvoicing()} icon={<SolutionOutlined />}>
                    <Link to={routeBulkInvoicing()}>Facturación masiva</Link>
                  </Menu.Item>
                )}

                {(canAccessBulkInvoicing || canAccessBulkCommonInvoicing || canAccessBulkActions) && (
                <Menu.Item key={routeUnifiedIssuing()} icon={<SolutionOutlined />}>
                  <Link to={routeUnifiedIssuing()}>Emisiones masivas</Link>
                </Menu.Item>
                )}

                {canAccessBulkCommonInvoicing && false && (
                  <Menu.Item key={routeBulkCommonInvoicing()} icon={<SolutionOutlined />}>
                    <Link to={routeBulkCommonInvoicing()}>Facturación normal masiva</Link>
                  </Menu.Item>
                )}

                {canAccessIssuancesInProgress && (
                  <Menu.Item key={routeIssuancesInProgress()} icon={<SolutionOutlined />}>
                    <Link to={routeIssuancesInProgress()}>Resumen de emisiones</Link>
                  </Menu.Item>
                )}

                {canAccessUploadCSDFiles && (
                  <Menu.Item key={routeDigitalStamps()} icon={<SolutionOutlined />}>
                    <Link to={routeDigitalStamps()}>Certificados digitales</Link>
                  </Menu.Item>
                )}

                {/* <Menu.Item key={routeDigitalStamps()} icon={<SolutionOutlined />}>
                  <Link to={routeDigitalStamps()}>Certificados digitales</Link>
                </Menu.Item> */}
              </Menu>

              <Button danger type="text" style={{ width: "100%", marginTop: "1.5rem" }} onClick={submitLogout}>
                Cerrar sesión&nbsp;
                <LogoutOutlined />
              </Button>
            </>
          )}
        </Layout.Sider>

        <Layout>
          <Layout.Content style={{ minHeight: "18rem", margin: "2rem 1rem 0" }}>{children}</Layout.Content>

          <Layout.Footer style={{ textAlign: "center" }}>
            <Popover
              title="About B2B Platform"
              content={
                <>
                  <p>
                    <b>Versión:</b> {WEB_VERSION_NUMBER}
                  </p>
                  <p>
                    <b>Fecha:</b> {WEB_VERSION_DATE}
                  </p>
                </>
              }
            >
              Powered by heru
            </Popover>
          </Layout.Footer>
        </Layout>
      </Layout>
    </Layout>
  );
}
